import { range } from "lodash-es";

import ChatFailureType from "@mapmycustomers/shared/enum/chat/ChatFailureType";
import ChatMessageRole from "@mapmycustomers/shared/enum/chat/ChatMessageRole";
import ChatUserMessageStatus from "@mapmycustomers/shared/enum/chat/ChatUserMessageStatus";
import ChatMessage from "@mapmycustomers/shared/types/chat/ChatMessage";
import User from "@mapmycustomers/shared/types/User";
import { isRecord } from "@mapmycustomers/shared/util/chat/chatWireGuards";

import retainChatThreads, {
  sliceChatThreads,
  StoredChatThread,
} from "@app/util/chat/chatThreadRetention";
import { APP_CHAT_THREAD_KEY } from "@app/util/chat/getChatThreadKey";
import { ChatProposalUiState } from "@app/util/chat/proposalRowStatus";
import loggingService from "@app/util/logging";

// sessionStorage rather than localStorage on purpose: a thread belongs to the tab, so a new tab
// starts clean and a stale thread cannot outlive the tab that held it.
export const CHAT_CONVERSATION_KEY = "__MMC_CHAT_CONVERSATION_V2__";

// Removed rather than migrated: V1's single conversation has no record to key it by.
const LEGACY_CHAT_CONVERSATION_KEY = "__MMC_CHAT_CONVERSATION_V1__";

export interface StoredChatConversation {
  byThread: Record<string, StoredChatThread>;
  proposalUi?: Record<number, ChatProposalUiState>;
  userId: User["id"];
}

const ROLES: string[] = [ChatMessageRole.ASSISTANT, ChatMessageRole.LOCAL, ChatMessageRole.USER];

// Written whole on every attempt, so left alone it only ever grows: evicting a thread drops its
// messages but not the rows the rep ticked on them, and a fallback that sheds threads then frees
// almost nothing. Scoped to what is actually being written instead.
const retainProposalUi = (
  byThread: Record<string, StoredChatThread>,
  proposalUi: Record<number, ChatProposalUiState> | undefined
): Record<number, ChatProposalUiState> | undefined => {
  if (!proposalUi) {
    return undefined;
  }
  const stored = new Set(
    Object.values(byThread).flatMap(({ messages }) =>
      messages.flatMap((message) =>
        "proposals" in message ? message.proposals.map(({ proposalId }) => proposalId) : []
      )
    )
  );
  return Object.fromEntries(
    Object.entries(proposalUi).filter(([proposalId]) => stored.has(Number(proposalId)))
  );
};

const isMessageShaped = (value: unknown): value is ChatMessage => {
  if (!isRecord(value)) {
    return false;
  }
  const { id, createdAt, role } = value;
  return typeof id === "string" && typeof createdAt === "string" && ROLES.includes(role as string);
};

// A message still marked sending was interrupted by the reload. The turn may well have
// completed server-side — BE commits the reply before writing the response, and there is no
// history endpoint to fetch it — so it is offered as retryable with its own failure type,
// which is what earns it a different tooltip from an outright failure.
const settleRestored = (message: ChatMessage): ChatMessage =>
  message.role === ChatMessageRole.USER && message.status === ChatUserMessageStatus.SENDING
    ? {
        ...message,
        failureType: ChatFailureType.RESTORED,
        status: ChatUserMessageStatus.FAILED,
      }
    : message;

// An empty thread is dropped: a record with no messages reads the same as one never asked about.
const parseThread = (value: unknown): StoredChatThread | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const messages = Array.isArray(value.messages)
    ? value.messages.filter(isMessageShaped).map(settleRestored)
    : [];
  if (!messages.length) {
    return undefined;
  }
  return {
    conversationId: typeof value.conversationId === "number" ? value.conversationId : undefined,
    messages,
  };
};

const parseThreads = (value: unknown): Record<string, StoredChatThread> => {
  if (!isRecord(value)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(value)
      .map(([threadKey, thread]) => [threadKey, parseThread(thread)] as const)
      .filter((entry): entry is [string, StoredChatThread] => entry[1] !== undefined)
  );
};

class ChatConversationStorage {
  clear = () => {
    try {
      sessionStorage.removeItem(CHAT_CONVERSATION_KEY);
      sessionStorage.removeItem(LEGACY_CHAT_CONVERSATION_KEY);
    } catch (error) {
      loggingService.error("Failed to clear the stored chat conversation", error);
    }
  };

  getState = (userId: User["id"]): Omit<StoredChatConversation, "userId"> | undefined => {
    try {
      // Reclaimed on the way past, so a V1 blob is not paying quota for bytes nothing can read.
      sessionStorage.removeItem(LEGACY_CHAT_CONVERSATION_KEY);
      const raw = sessionStorage.getItem(CHAT_CONVERSATION_KEY);
      if (typeof raw !== "string") {
        return undefined;
      }
      const parsed: unknown = JSON.parse(raw);
      if (!isRecord(parsed)) {
        return undefined;
      }
      // The second of the two user-change guards: this one holds even when the first (the
      // prevUserId check in store/app/sagas.ts) is not reached, and onInitialize has
      // several early returns.
      if (parsed.userId !== userId) {
        return undefined;
      }
      return {
        byThread: parseThreads(parsed.byThread),
        proposalUi: isRecord(parsed.proposalUi)
          ? (parsed.proposalUi as Record<number, ChatProposalUiState>)
          : {},
      };
    } catch (error) {
      // Logged like setState and clear are: corrupt stored JSON means the rep silently loses a
      // thread, and swallowing that leaves nothing to explain why.
      loggingService.error("Failed to read the stored chat conversation", error);
      return undefined;
    }
  };

  setState = ({ byThread, proposalUi, userId }: StoredChatConversation) => {
    const retained = retainChatThreads(byThread);
    const hasAppThread = APP_CHAT_THREAD_KEY in retained;
    const recordThreadCount = Object.keys(retained).length - (hasAppThread ? 1 : 0);
    const fewestRecordThreads = hasAppThread || recordThreadCount === 0 ? 0 : 1;
    let lastError: unknown;
    const write = (threads: Record<string, StoredChatThread>) => {
      try {
        sessionStorage.setItem(
          CHAT_CONVERSATION_KEY,
          JSON.stringify({
            byThread: threads,
            proposalUi: retainProposalUi(threads, proposalUi),
            userId,
          })
        );
        return true;
      } catch (error) {
        lastError = error;
        return false;
      }
    };
    const attempt = (maxThreads: number) => write(retainChatThreads(retained, maxThreads));
    // Each attempt keeps one fewer thread, newest first. Threads reach the quota far sooner than
    // the single conversation did, and the browser refusing the whole write would cost the rep
    // every thread at once.
    const storedThreadCount = range(recordThreadCount, fewestRecordThreads - 1, -1).find(attempt);
    if (storedThreadCount === undefined) {
      // Whole threads have run out of room, so the last attempt keeps the newest thread and the
      // rep's own, sliced to their most recent messages. Half a conversation is the thing every
      // attempt above this one avoids, and it is still better than the alternative here, which is
      // the rep reloading into an empty chat they never cleared.
      if (write(sliceChatThreads(retainChatThreads(retained, 1)))) {
        loggingService.warning("Sliced the chat threads to fit browser storage");
        return;
      }
      // Quota exceeded even then, or Safari private browsing. Degrade to "not persisted this
      // session" rather than letting the throw escape into the saga that called us.
      loggingService.error("Failed to persist the chat conversation", lastError);
      return;
    }
    if (storedThreadCount < recordThreadCount) {
      loggingService.warning("Evicted chat threads to fit browser storage", {
        evicted: recordThreadCount - storedThreadCount,
        kept: storedThreadCount,
      });
    }
  };
}

const chatConversationStorage = new ChatConversationStorage();
export default chatConversationStorage;
